import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import useCustomerAuth from '../hooks/useCustomerAuth';
import useOtpResendCooldown from '../hooks/useOtpResendCooldown';
import { requestCustomerOtp, verifyCustomerOtp } from '../api/customer';
import { useBookingContext } from '../context/BookingContext';
import { useStoreSelector } from '../hooks/useStoreSelector';
import useBookingStore, {
  selectStayId,
  selectStayData,
  selectCheckIn,
  selectCheckOut,
  selectNights,
  selectGuests,
  selectRoomTypeId,
  selectRoomCount,
  selectMealPlanId,
  selectQuoteId,
  selectRatePlanCode,
  selectSelectedAddons,
  selectCardOfferId,
  selectPromoCode,
  selectPricingBreakdown,
  selectBookingId,
  selectGuestDetails,
  selectSetGuestDetails,
  selectSetBookingId,
  selectSetPricingBreakdown,
  selectSetStayData,
} from '../store/bookingStore';
import {
  addBookingAddons,
  applyBookingCardOffer,
  applyBookingCoupon,
  createDraftBooking,
  getBookingById,
  saveGuestDetails,
} from '../api/booking';
import { getCheckoutStayData } from '../api/propertyDetail';
import { normalizeBookingDraft } from '../utils/bookingMapper';
import { normalizeBookingPricing } from '../../utils/bookingPricing';
import CartHeader from '../components/cart/CartHeader';
import StayCard from '../components/cart/StayCard';
import SummaryCard from '../components/cart/SummaryCard';
import MobileStickyBar from '../components/cart/MobileStickyBar';
import { formatInr } from '../utils/bookingUtils';
import '../../mobile/styles/mobile-theme.css';

const arrivalTimeRegex = /^(?:([01]?\d|2[0-3]):[0-5]\d|(?:0?[1-9]|1[0-2]):[0-5]\d\s?(AM|PM))$/i;

const guestSchema = z.object({
  title: z.string().trim().optional(),
  firstName: z.string().trim().min(2, 'Please enter guest first name.').max(40, 'First name must be 40 characters or fewer.'),
  lastName: z.string().trim().min(1, 'Please enter guest last name.').max(40, 'Last name must be 40 characters or fewer.'),
  email: z.string().trim().email('Enter a valid email address.'),
  countryCode: z.string().trim().optional(),
  phoneNumber: z.preprocess(
    (value) => String(value || '').replace(/\D/g, ''),
    z.string().regex(/^[0-9]{10}$/, 'Enter a valid 10-digit phone number.')
  ),
  arrivalTime: z.string().trim().optional().refine((value) => !value || arrivalTimeRegex.test(value), {
    message: 'Enter a valid arrival time (e.g., 3:00 PM or 15:00).',
  }),
  specialRequests: z.string().trim().max(500, 'Special requests must be 500 characters or fewer.').optional(),
});

const splitName = (value) => {
  const cleaned = String(value || '').trim();
  if (!cleaned) return { firstName: '', lastName: '' };
  const parts = cleaned.split(/\s+/);
  const firstName = parts.shift() || '';
  return { firstName, lastName: parts.join(' ') };
};

const buildFullName = (firstName, lastName) =>
  [String(firstName || '').trim(), String(lastName || '').trim()].filter(Boolean).join(' ').trim();

const calcNights = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return 0;
  const start = new Date(`${checkIn}T00:00:00`);
  const end = new Date(`${checkOut}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(0, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
};

const normalizeAdditionalGuests = (list = []) =>
  Array.isArray(list)
    ? list
        .map((guest) => ({
          title: String(guest?.title || '').trim(),
          firstName: String(guest?.firstName || '').trim(),
          lastName: String(guest?.lastName || '').trim(),
        }))
        .filter((guest) => guest.firstName || guest.lastName)
    : [];

const buildAdditionalGuestsNote = (list = []) =>
  normalizeAdditionalGuests(list)
    .map((guest, idx) => {
      const name = buildFullName(guest.firstName, guest.lastName);
      return `${idx + 1}) ${guest.title ? `${guest.title} ` : ''}${name}`.trim();
    })
    .filter(Boolean)
    .join(' | ');

const mergeSpecialRequests = (base, additionalNote) => {
  const trimmed = String(base || '').trim();
  if (!additionalNote) return trimmed || undefined;
  const prefix = 'Additional guests: ';
  const combined = trimmed ? `${trimmed}\n${prefix}${additionalNote}` : `${prefix}${additionalNote}`;
  if (combined.length <= 500) return combined;
  const head = trimmed ? `${trimmed}\n${prefix}` : prefix;
  const available = 500 - head.length;
  if (available <= 0) return trimmed || undefined;
  return `${head}${additionalNote.slice(0, available)}`;
};

const extractBookingId = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (typeof value === 'object') {
    const nested =
      value.id ||
      value.bookingId ||
      value.booking_id ||
      value.bookingCode ||
      value.booking_code ||
      value.bookingRef ||
      value.booking_ref ||
      value.code;
    if (nested === value) return '';
    return extractBookingId(nested);
  }
  return '';
};

const hasUsefulGuestDetails = (value = {}) =>
  Boolean(
    String(value?.fullName || '').trim() ||
      String(value?.firstName || '').trim() ||
      String(value?.lastName || '').trim() ||
      String(value?.email || '').trim() ||
      String(value?.phone || value?.phoneNumber || '').trim()
  );

const GuestDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerAuth = useCustomerAuth();
  const { accessToken, customer } = customerAuth;
  const stayId = useStoreSelector(useBookingStore, selectStayId);
  const stayData = useStoreSelector(useBookingStore, selectStayData);
  const checkIn = useStoreSelector(useBookingStore, selectCheckIn);
  const checkOut = useStoreSelector(useBookingStore, selectCheckOut);
  const nights = useStoreSelector(useBookingStore, selectNights);
  const guests = useStoreSelector(useBookingStore, selectGuests);
  const roomTypeId = useStoreSelector(useBookingStore, selectRoomTypeId);
  const roomCount = useStoreSelector(useBookingStore, selectRoomCount);
  const mealPlanId = useStoreSelector(useBookingStore, selectMealPlanId);
  const quoteId = useStoreSelector(useBookingStore, selectQuoteId);
  const ratePlanCode = useStoreSelector(useBookingStore, selectRatePlanCode);
  const selectedAddons = useStoreSelector(useBookingStore, selectSelectedAddons);
  const cardOfferId = useStoreSelector(useBookingStore, selectCardOfferId);
  const promoCode = useStoreSelector(useBookingStore, selectPromoCode);
  const pricingBreakdown = useStoreSelector(useBookingStore, selectPricingBreakdown);
  const bookingId = useStoreSelector(useBookingStore, selectBookingId);
  const guestDetails = useStoreSelector(useBookingStore, selectGuestDetails);
  const setGuestDetails = useStoreSelector(useBookingStore, selectSetGuestDetails);
  const setBookingId = useStoreSelector(useBookingStore, selectSetBookingId);
  const setPricingBreakdown = useStoreSelector(useBookingStore, selectSetPricingBreakdown);
  const setStayData = useStoreSelector(useBookingStore, selectSetStayData);
  const { bookingId: contextBookingId, draft, setBookingId: setContextBookingId, setDraft } = useBookingContext();

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting },
    reset,
    setValue,
    getValues,
    clearErrors,
    trigger,
  } = useForm({
    resolver: zodResolver(guestSchema),
    mode: 'onChange',
    defaultValues: (() => {
      const seed = guestDetails.fullName || draft?.guestDetails?.fullName || '';
      const split = splitName(seed);
      return {
        title: guestDetails.title || draft?.guestDetails?.title || 'Mr',
        firstName: guestDetails.firstName || draft?.guestDetails?.firstName || split.firstName,
        lastName: guestDetails.lastName || draft?.guestDetails?.lastName || split.lastName,
        email: guestDetails.email || draft?.guestDetails?.email || '',
        countryCode: guestDetails.countryCode || draft?.guestDetails?.countryCode || '+91',
        phoneNumber: guestDetails.phone || draft?.guestDetails?.phone || '',
        arrivalTime: guestDetails.arrivalTime || draft?.guestDetails?.arrivalTime || '',
        specialRequests: guestDetails.specialRequests || draft?.guestDetails?.specialRequests || '',
      };
    })(),
  });

  const [submitError, setSubmitError] = useState('');
  const [loadingBooking, setLoadingBooking] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const isMountedRef = useRef(true);
  const loadedStayIdRef = useRef('');
  const draftRequestRef = useRef(false);
  const queryBookingId = searchParams.get('bookingId') || '';
  const hasSelection = Boolean(stayId || checkIn || checkOut);
  const draftMatchesSelection = useMemo(() => {
    if (!draft || !hasSelection) return Boolean(draft);
    const selectionStayId = String(stayId || '').trim();
    const draftStayId = String(draft?.stayId || '').trim();
    const stayMatches =
      !selectionStayId || !draftStayId || selectionStayId === draftStayId;
    const checkInMatches = !checkIn || !draft?.checkIn || checkIn === draft.checkIn;
    const checkOutMatches = !checkOut || !draft?.checkOut || checkOut === draft.checkOut;
    return stayMatches && checkInMatches && checkOutMatches;
  }, [draft, hasSelection, stayId, checkIn, checkOut]);
  const effectiveBookingId = queryBookingId || (draftMatchesSelection ? (contextBookingId || bookingId || draft?.id || '') : '');
  const effectiveStayId = stayId || (draftMatchesSelection ? draft?.stayId : '');
  const isProviderStay = String(effectiveStayId || '').startsWith('provider:');
  const editLink = stayData?.slug || effectiveStayId ? `/property/${stayData?.slug || effectiveStayId}` : '';
  const selectedNights = useMemo(() => calcNights(checkIn, checkOut), [checkIn, checkOut]);
  const minimumStayNights = Math.max(1, Number(stayData?.min_nights || 1) || 1);
  const loginRedirect = encodeURIComponent(`${location.pathname}${location.search}`);
  const loginHref = `/customer/login?next=${loginRedirect}`;
  const showLoginPrompt = !customerAuth.loading && !customer;
  const selectedAddonIdsKey = useMemo(
    () =>
      (selectedAddons || [])
        .map((addon) => String(addon?.id || '').trim())
        .filter(Boolean)
        .sort()
        .join(','),
    [selectedAddons]
  );
  const [additionalGuests, setAdditionalGuests] = useState(() =>
    normalizeAdditionalGuests(draft?.guestDetails?.additionalGuests || guestDetails.additionalGuests || [])
  );
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginIdentifier, setLoginIdentifier] = useState('phone');
  const [loginCountryCode, setLoginCountryCode] = useState('+91');
  const [loginMobile, setLoginMobile] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginFullName, setLoginFullName] = useState('');
  const [loginOtp, setLoginOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loginRequiresSignup, setLoginRequiresSignup] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginSuccess, setLoginSuccess] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const { resendSeconds, resendBlocked, startResendCooldown, resetResendCooldown } = useOtpResendCooldown();
  const [isMobileFlow, setIsMobileFlow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobileFlow(query.matches);
    update();
    if (query.addEventListener) {
      query.addEventListener('change', update);
      return () => query.removeEventListener('change', update);
    }
    query.addListener(update);
    return () => query.removeListener(update);
  }, []);

  useEffect(() => {
    if (queryBookingId) return;
    if (!draft || draftMatchesSelection) return;
    setContextBookingId('');
    setBookingId('');
    setDraft(null);
  }, [draft, draftMatchesSelection, queryBookingId, setBookingId, setContextBookingId, setDraft]);

  const applyBookingResponse = useCallback(
    (booking) => {
      const normalized = normalizeBookingDraft(booking, stayData);
      if (!normalized) return null;
      if (normalized.id) {
        setBookingId(normalized.id);
        setContextBookingId(normalized.id);
      }
      setDraft(normalized);
      if (normalized.pricing) {
        setPricingBreakdown({
          base: normalized.pricing.base,
          addons: normalized.pricing.addons,
          fees: normalized.pricing.fees,
          taxes: normalized.pricing.tax,
          discount: normalized.pricing.discount,
          total: normalized.pricing.total,
          nights: normalized.nights,
          currency: normalized.pricing.currency,
        });
      }
      if (hasUsefulGuestDetails(normalized.guestDetails)) {
        setGuestDetails(normalized.guestDetails);
      }

      const normalizedStayId = normalized.stayId ? String(normalized.stayId) : '';
      const currentStayId = stayData?.id ? String(stayData.id) : '';
      const stayPatch = {
        ...(currentStayId && currentStayId === normalizedStayId ? stayData || {} : {}),
        id: normalized.stayId || stayData?.id || '',
        slug:
          booking?.property_slug ||
          stayData?.slug ||
          normalized.stayId ||
          '',
        title:
          booking?.property_name ||
          normalized.stayName ||
          stayData?.title ||
          stayData?.name ||
          '',
        name:
          booking?.property_name ||
          normalized.stayName ||
          stayData?.name ||
          stayData?.title ||
          '',
        city: booking?.property_city || stayData?.city || '',
        area: booking?.property_area || stayData?.area || '',
        location: normalized.location || stayData?.location || '',
        coverImage:
          booking?.property_image ||
          booking?.image_url ||
          stayData?.coverImage ||
          stayData?.featured_image ||
          stayData?.image_url ||
          '',
      };
      if (stayPatch.id || stayPatch.title || stayPatch.coverImage) {
        setStayData(stayPatch);
      }
      return normalized;
    },
    [
      setBookingId,
      setContextBookingId,
      setDraft,
      setPricingBreakdown,
      setGuestDetails,
      setStayData,
      stayData,
    ]
  );

  useEffect(() => {
    if (queryBookingId && !bookingId && !contextBookingId) {
      setBookingId(queryBookingId);
      setContextBookingId(queryBookingId);
    }
  }, [queryBookingId, bookingId, contextBookingId, setBookingId, setContextBookingId]);

  useEffect(() => {
    const draftId = extractBookingId(draft?.id);
    if (!draftId) return;
    if (!bookingId) {
      setBookingId(draftId);
    }
    if (!contextBookingId) {
      setContextBookingId(draftId);
    }
  }, [bookingId, contextBookingId, draft?.id, setBookingId, setContextBookingId]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (effectiveBookingId) return;
    if (draftRequestRef.current) return;
    if (!effectiveStayId || !checkIn || !checkOut) return;
    if (selectedNights && selectedNights < minimumStayNights) {
      setSubmitError(`Minimum stay is ${minimumStayNights} night${minimumStayNights > 1 ? 's' : ''}. Please edit dates and try again.`);
      return;
    }
    const createDraft = async () => {
      setCreatingDraft(true);
      draftRequestRef.current = true;
      setSubmitError('');
      try {
        let booking = await createDraftBooking(
          {
            stayId: effectiveStayId,
            checkIn,
            checkOut,
            guests: Number(guests || 1),
            roomTypeId: roomTypeId || undefined,
            roomCount: Number(roomCount || 1) || 1,
            mealPlanId: mealPlanId || undefined,
            cardOfferId: cardOfferId || undefined,
            quoteId: isProviderStay ? quoteId || undefined : undefined,
            ratePlanCode: isProviderStay ? ratePlanCode || mealPlanId || undefined : undefined,
          },
          accessToken
        );
        const initialNormalized = applyBookingResponse(booking);
        const draftId = extractBookingId(initialNormalized?.id) || extractBookingId(booking);
        const addonIds = selectedAddonIdsKey ? selectedAddonIdsKey.split(',') : [];
        if (addonIds.length && draftId) {
          try {
            booking = await addBookingAddons(draftId, { addonIds }, accessToken);
            applyBookingResponse(booking);
          } catch (err) {
            setSubmitError(err.message || 'Selected add-ons could not be applied.');
          }
        }
        if (cardOfferId && draftId) {
          try {
            booking = await applyBookingCardOffer(draftId, { cardOfferId }, accessToken);
            applyBookingResponse(booking);
          } catch (err) {
            setSubmitError(err.message || 'Selected card offer could not be applied.');
          }
        }
        const promo = (promoCode || '').trim().toUpperCase();
        if (promo && draftId) {
          try {
            booking = await applyBookingCoupon(draftId, { code: promo }, accessToken);
            applyBookingResponse(booking);
          } catch (err) {
            setSubmitError(err.message || 'Promo code could not be applied.');
          }
        }
        if (!isMountedRef.current) return;
        const normalized = applyBookingResponse(booking);
        const newId = extractBookingId(normalized?.id) || extractBookingId(booking);
        if (newId) {
          const nextParams = new URLSearchParams(location.search);
          nextParams.set('bookingId', String(newId));
          setSearchParams(nextParams, { replace: true });
        }
      } catch (err) {
        if (!isMountedRef.current) return;
        setSubmitError(err.message || 'Unable to start the booking. Please try again.');
      } finally {
        if (isMountedRef.current) {
          setCreatingDraft(false);
        }
        draftRequestRef.current = false;
      }
    };
    createDraft();
  }, [
    accessToken,
    applyBookingResponse,
    cardOfferId,
    checkIn,
    checkOut,
    effectiveBookingId,
    effectiveStayId,
    guests,
    isProviderStay,
    minimumStayNights,
    promoCode,
    quoteId,
    ratePlanCode,
    roomCount,
    roomTypeId,
    mealPlanId,
    selectedNights,
    selectedAddonIdsKey,
    location.search,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!effectiveBookingId || draft?.id || loadingBooking) return;
    let active = true;
    setLoadingBooking(true);
    setSubmitError('');
    getBookingById(effectiveBookingId, accessToken)
      .then((booking) => {
        if (!active) return;
        applyBookingResponse(booking);
      })
      .catch((err) => {
        if (!active) return;
        setSubmitError(err.message || 'Failed to load booking.');
      })
      .finally(() => {
        if (active) setLoadingBooking(false);
      });
    return () => {
      active = false;
    };
  }, [accessToken, applyBookingResponse, draft?.id, effectiveBookingId, loadingBooking]);

  useEffect(() => {
    const next = draft?.guestDetails || guestDetails;
    if (!hasUsefulGuestDetails(next)) return;
    const current = getValues();
    const split = splitName(next?.fullName || '');
    reset({
      title: next?.title || 'Mr',
      firstName: next?.firstName || split.firstName || current.firstName || '',
      lastName: next?.lastName || split.lastName || current.lastName || '',
      email: next?.email || current.email || '',
      countryCode: next?.countryCode || '+91',
      phoneNumber: next?.phone || current.phoneNumber || '',
      arrivalTime: next?.arrivalTime || current.arrivalTime || '',
      specialRequests: next?.specialRequests || current.specialRequests || '',
    }, { keepDirtyValues: true, keepErrors: false });
    setAdditionalGuests(normalizeAdditionalGuests(next?.additionalGuests || []));
  }, [draft?.guestDetails, getValues, guestDetails, reset]);

  useEffect(() => {
    if (!loginOpen) return;
    const current = getValues();
    setLoginCountryCode(current.countryCode || '+91');
    setLoginMobile(String(current.phoneNumber || '').replace(/\D/g, ''));
    setLoginEmail(current.email || '');
    setLoginIdentifier('phone');
    setLoginFullName(buildFullName(current.firstName, current.lastName));
    setLoginOtp('');
    setOtpSent(false);
    setLoginRequiresSignup(false);
    setLoginError('');
    setLoginSuccess('');
    setLoginLoading(false);
  }, [getValues, loginOpen]);

  const pricing = useMemo(() => {
    const source = draft?.pricing || pricingBreakdown;
    if (!source) return null;
    const normalized = normalizeBookingPricing(source, {
      nights: draft?.nights || pricingBreakdown?.nights || nights || 0,
      currency: source.currency || stayData?.currency || 'INR',
    });
    const explicitTotal = Number(
      source.finalPayable ??
        source.final_payable ??
        source.total ??
        normalized.finalPayable
    );
    return {
      base: normalized.basePrice,
      basePrice: normalized.basePrice,
      addons: Number(source.addons ?? pricingBreakdown?.addons ?? 0) || 0,
      fees: normalized.mandatoryFees,
      mandatoryFees: normalized.mandatoryFees,
      tax: normalized.gstAmount,
      gstAmount: normalized.gstAmount,
      discount: normalized.discountAmount,
      discountAmount: normalized.discountAmount,
      taxableSubtotal: normalized.taxableSubtotal,
      total: explicitTotal,
      finalPayable: explicitTotal,
      currency: normalized.currency,
      breakdownItems: normalized.breakdownItems,
    };
  }, [draft?.nights, draft?.pricing, nights, pricingBreakdown, stayData?.currency]);

  const lineItems = useMemo(() => {
    if (!pricing) return [];
    if (Array.isArray(pricing.breakdownItems) && pricing.breakdownItems.length) {
      const rows = [];
      pricing.breakdownItems.forEach((item) => {
        if (/final payable amount/i.test(String(item.label || ''))) return;
        rows.push({ label: item.label, value: item.amount });
        if (/taxable subtotal/i.test(String(item.label || '')) && pricing.addons) {
          rows.push({ label: 'Add-ons', value: pricing.addons });
        }
      });
      return rows;
    }
    const rows = [{ label: 'Base Price', value: pricing.basePrice }];
    if (pricing.discount) rows.push({ label: 'Discount', value: -Math.abs(pricing.discount) });
    rows.push({ label: 'Taxable Subtotal', value: pricing.taxableSubtotal });
    if (pricing.addons) rows.push({ label: 'Add-ons', value: pricing.addons });
    if (pricing.fees) rows.push({ label: 'Mandatory fees', value: pricing.fees });
    if (pricing.tax) rows.push({ label: 'GST', value: pricing.tax });
    return rows;
  }, [pricing]);

  const totalPayable = pricing?.total || 0;
  const pricingCurrency = pricing?.currency || stayData?.currency || 'INR';
  const canContinue = !isSubmitting;
  const loginReady = (() => {
    const phoneReady = /^\d{10}$/.test(String(loginMobile || '').replace(/\D/g, ''));
    const emailReady = Boolean(loginEmail && loginEmail.includes('@'));
    if (loginRequiresSignup) {
      return Boolean(loginFullName.trim()) && phoneReady && emailReady && (!otpSent || String(loginOtp || '').trim().length >= 4);
    }
    const identifierReady = loginIdentifier === 'email' ? emailReady : phoneReady;
    return otpSent ? identifierReady && String(loginOtp || '').trim().length >= 4 : identifierReady;
  })();

  const handleAddGuest = () => {
    setAdditionalGuests((prev) => [...prev, { title: 'Mr', firstName: '', lastName: '' }]);
  };

  const handleRemoveGuest = (index) => {
    setAdditionalGuests((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleGuestChange = (index, field, value) => {
    setAdditionalGuests((prev) =>
      prev.map((guest, idx) => (idx === index ? { ...guest, [field]: value } : guest))
    );
  };

  useEffect(() => {
    if (!effectiveStayId) return;
    const stayKey = String(effectiveStayId);
    const matchesStay =
      stayData && (String(stayData.id) === stayKey || String(stayData.slug) === stayKey);
    if (matchesStay) {
      loadedStayIdRef.current = stayKey;
      return;
    }
    if (loadedStayIdRef.current === stayKey) return;
    loadedStayIdRef.current = stayKey;
    let active = true;
    const loadStay = async () => {
      try {
        const res = await getCheckoutStayData(effectiveStayId);
        if (!active) return;
        setStayData({
          ...res,
          id: res.id || res.property_id || effectiveStayId,
          slug: res.slug || res.id || effectiveStayId,
          coverImage: res.coverImage || res.featured_image || res.image_url,
        });
      } catch (_) {
        // non-blocking
      }
    };
    loadStay();
    return () => {
      active = false;
    };
  }, [stayData, effectiveStayId, setStayData]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleContinue = handleSubmit(async (values) => {
    setSubmitError('');
    const normalizedValues = {
      ...values,
      firstName: String(values.firstName || '').trim(),
      lastName: String(values.lastName || '').trim(),
      email: String(values.email || '').trim(),
      phoneNumber: String(values.phoneNumber || '').replace(/\D/g, ''),
      countryCode: String(values.countryCode || '+91').trim() || '+91',
      arrivalTime: String(values.arrivalTime || '').trim(),
      specialRequests: String(values.specialRequests || '').trim(),
    };
    Object.entries(normalizedValues).forEach(([key, value]) => {
      setValue(key, value, { shouldDirty: true, shouldValidate: false });
    });
    clearErrors(['firstName', 'lastName', 'email', 'phoneNumber', 'countryCode', 'arrivalTime', 'specialRequests']);
    const formValid = await trigger(['firstName', 'lastName', 'email', 'phoneNumber', 'countryCode', 'arrivalTime', 'specialRequests']);
    if (!formValid) return;
    values = normalizedValues;
    setGuestDetails({
      fullName: buildFullName(values.firstName, values.lastName),
      title: values.title,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phoneNumber,
      countryCode: values.countryCode,
      arrivalTime: values.arrivalTime,
      specialRequests: values.specialRequests,
      additionalGuests,
    });
    if (creatingDraft) {
      setSubmitError('Booking details are being prepared. Please try again in a moment.');
      return;
    }
    if (!pricing) {
      setSubmitError('Pricing details are still loading. Please try again in a moment.');
      return;
    }
    let targetBookingId = effectiveBookingId;
    if (!targetBookingId && effectiveStayId && checkIn && checkOut) {
      try {
        let booking = await createDraftBooking(
          {
            stayId: effectiveStayId,
            checkIn,
            checkOut,
            guests: Number(guests || 1),
            roomTypeId: roomTypeId || undefined,
            roomCount: Number(roomCount || 1) || 1,
            mealPlanId: mealPlanId || undefined,
            cardOfferId: cardOfferId || undefined,
            quoteId: isProviderStay ? quoteId || undefined : undefined,
            ratePlanCode: isProviderStay ? ratePlanCode || mealPlanId || undefined : undefined,
          },
          accessToken
        );
        const initialNormalized = applyBookingResponse(booking);
        const draftId = extractBookingId(initialNormalized?.id) || extractBookingId(booking);
        const addonIds = (selectedAddons || []).map((addon) => addon.id).filter(Boolean);
        if (addonIds.length && draftId) {
          try {
            booking = await addBookingAddons(draftId, { addonIds }, accessToken);
            applyBookingResponse(booking);
          } catch (err) {
            setSubmitError(err.message || 'Selected add-ons could not be applied.');
          }
        }
        if (cardOfferId && draftId) {
          try {
            booking = await applyBookingCardOffer(draftId, { cardOfferId }, accessToken);
            applyBookingResponse(booking);
          } catch (err) {
            setSubmitError(err.message || 'Selected card offer could not be applied.');
          }
        }
        const promo = (promoCode || '').trim().toUpperCase();
        if (promo && draftId) {
          try {
            booking = await applyBookingCoupon(draftId, { code: promo }, accessToken);
            applyBookingResponse(booking);
          } catch (err) {
            setSubmitError(err.message || 'Promo code could not be applied.');
          }
        }
        const normalized = applyBookingResponse(booking);
        targetBookingId = extractBookingId(normalized?.id) || extractBookingId(booking);
        if (targetBookingId) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('bookingId', String(targetBookingId));
          setSearchParams(nextParams, { replace: true });
        }
      } catch (err) {
        setSubmitError(err.message || 'Unable to start the booking. Please try again.');
        return;
      }
    }
    if (!targetBookingId) {
      setSubmitError('Booking details are missing. Return to stays to restart.');
      return;
    }
    try {
      const fullName = buildFullName(values.firstName, values.lastName);
      const cleanedPhone = String(values.phoneNumber || '').replace(/\D/g, '');
      const guestListNote = buildAdditionalGuestsNote(additionalGuests);
      const mergedSpecial = mergeSpecialRequests(values.specialRequests, guestListNote);
      const payload = {
        fullName,
        email: values.email.trim(),
        phone: cleanedPhone,
        arrivalTime: values.arrivalTime?.trim() || undefined,
        specialRequests: mergedSpecial || undefined,
      };
      const response = await saveGuestDetails(targetBookingId, payload, accessToken);
      const normalized = applyBookingResponse(response);
      setGuestDetails({
        ...payload,
        title: values.title,
        firstName: values.firstName,
        lastName: values.lastName,
        countryCode: values.countryCode,
        additionalGuests,
      });
      if (!normalized?.id) {
        setSubmitError('Booking id missing from response.');
        return;
      }
      navigate('/booking/review');
    } catch (err) {
      setSubmitError(err.message || 'Failed to continue to review.');
    }
  });

  const applyCustomerToForm = useCallback((cust) => {
    if (!cust) return;
    const fullName = cust.full_name || cust.name || '';
    const split = splitName(fullName);
    if (cust.country_code) {
      setValue('countryCode', cust.country_code, { shouldDirty: true, shouldValidate: true });
    }
    if (cust.phone) {
      setValue('phoneNumber', String(cust.phone).replace(/\D/g, ''), { shouldDirty: true, shouldValidate: true });
    }
    if (cust.email && !getValues('email')) {
      setValue('email', cust.email, { shouldDirty: true, shouldValidate: true });
    }
    if (split.firstName && !getValues('firstName')) {
      setValue('firstName', split.firstName, { shouldDirty: true, shouldValidate: true });
    }
    if (split.lastName && !getValues('lastName')) {
      setValue('lastName', split.lastName, { shouldDirty: true, shouldValidate: true });
    }
  }, [getValues, setValue]);

  useEffect(() => {
    if (!customer) return;
    applyCustomerToForm(customer);
  }, [customer, applyCustomerToForm]);

  const handleOtpRequest = async () => {
    const formPhone = String(getValues('phoneNumber') || '').replace(/\D/g, '');
    const trimmedPhone = String(loginMobile || '').replace(/\D/g, '') || formPhone;
    const requestCountryCode = loginCountryCode || getValues('countryCode') || '+91';
    const requestEmail = loginEmail?.trim() || getValues('email')?.trim() || '';
    const requestFullName = loginFullName.trim() || buildFullName(getValues('firstName'), getValues('lastName'));
    if (loginIdentifier === 'phone' && !/^\d{10}$/.test(trimmedPhone)) {
      setLoginError('Enter a valid 10-digit mobile number.');
      return;
    }
    if (loginIdentifier === 'email' && !requestEmail.includes('@')) {
      setLoginError('Enter a valid email address.');
      return;
    }
    if (loginRequiresSignup && (!requestFullName || !/^\d{10}$/.test(trimmedPhone) || !requestEmail.includes('@'))) {
      setLoginError('Full name, mobile number and email are required for signup.');
      return;
    }
    setLoginError('');
    setLoginSuccess('');
    setLoginLoading(true);
    try {
      const res = await requestCustomerOtp({
        identifier_type: loginIdentifier,
        phone: trimmedPhone,
        country_code: requestCountryCode,
        email: requestEmail || undefined,
        full_name: requestFullName || undefined,
      });
      setOtpSent(true);
      startResendCooldown();
      setLoginRequiresSignup(Boolean(res?.requiresSignup));
      setLoginSuccess(res?.delivery?.warning || res?.message || 'OTP sent.');
    } catch (err) {
      if (err.message?.toLowerCase().includes('signup') || err.message?.toLowerCase().includes('account not found')) {
        setLoginRequiresSignup(true);
        setLoginError('Account not found. Enter full name, mobile and email to sign up.');
      } else {
        setLoginError(err.message || 'Unable to send OTP.');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleOtpResend = async () => {
    if (resendBlocked) return;
    await handleOtpRequest();
  };

  const handleOtpVerify = async () => {
    const formPhone = String(getValues('phoneNumber') || '').replace(/\D/g, '');
    const trimmedPhone = String(loginMobile || '').replace(/\D/g, '') || formPhone;
    const requestCountryCode = loginCountryCode || getValues('countryCode') || '+91';
    const requestEmail = loginEmail?.trim() || getValues('email')?.trim() || '';
    const requestFullName = loginFullName.trim() || buildFullName(getValues('firstName'), getValues('lastName'));
    if (loginIdentifier === 'phone' && !/^\d{10}$/.test(trimmedPhone)) {
      setLoginError('Enter a valid 10-digit mobile number.');
      return;
    }
    if (loginIdentifier === 'email' && !requestEmail.includes('@')) {
      setLoginError('Enter a valid email address.');
      return;
    }
    if (loginRequiresSignup && (!requestFullName || !/^\d{10}$/.test(trimmedPhone) || !requestEmail.includes('@'))) {
      setLoginError('Full name, mobile number and email are required for signup.');
      return;
    }
    if (!loginOtp || String(loginOtp).trim().length < 4) {
      setLoginError('Enter the OTP.');
      return;
    }
    setLoginError('');
    setLoginLoading(true);
    try {
      const res = await verifyCustomerOtp({
        identifier_type: loginIdentifier,
        phone: trimmedPhone,
        country_code: requestCountryCode,
        otp: String(loginOtp).trim(),
        email: requestEmail || undefined,
        full_name: requestFullName || undefined,
      });
      const authenticatedCustomer = await customerAuth.applyAuth(res);
      const fallbackCustomer = {
        ...(res?.customer || {}),
        ...(authenticatedCustomer || {}),
        full_name:
          authenticatedCustomer?.full_name ||
          authenticatedCustomer?.name ||
          res?.customer?.full_name ||
          res?.customer?.name ||
          requestFullName,
        name:
          authenticatedCustomer?.name ||
          res?.customer?.name ||
          requestFullName,
        email: authenticatedCustomer?.email || res?.customer?.email || requestEmail,
        phone: authenticatedCustomer?.phone || res?.customer?.phone || trimmedPhone,
        country_code:
          authenticatedCustomer?.country_code ||
          authenticatedCustomer?.countryCode ||
          res?.customer?.country_code ||
          requestCountryCode,
      };
      if (!authenticatedCustomer && fallbackCustomer) {
        customerAuth.setCustomer(fallbackCustomer);
      }
      applyCustomerToForm(fallbackCustomer);
      const split = splitName(requestFullName);
      if (split.firstName) {
        setValue('firstName', split.firstName, { shouldDirty: true, shouldValidate: true });
      }
      if (split.lastName) {
        setValue('lastName', split.lastName, { shouldDirty: true, shouldValidate: true });
      }
      setValue('countryCode', requestCountryCode, { shouldDirty: true, shouldValidate: true });
      setValue('phoneNumber', trimmedPhone, { shouldDirty: true, shouldValidate: true });
      if (requestEmail) {
        setValue('email', requestEmail, { shouldDirty: true, shouldValidate: true });
      }
      setLoginOpen(false);
    } catch (err) {
      setLoginError(err.message || 'OTP verification failed.');
    } finally {
      setLoginLoading(false);
    }
  };

  if (!effectiveStayId && !loadingBooking) {
    return (
      <div className="min-h-screen bg-[#F6F2EC] px-4 py-10">
        <div className="mx-auto max-w-4xl rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center">
          <h2 className="text-2xl font-semibold text-slate-900">Guest details need a booking</h2>
          <p className="mt-2 text-sm text-slate-600">Head back to stays to resume your booking.</p>
          <button
            type="button"
            onClick={() => navigate('/listings')}
            className="mt-6 rounded-full bg-[#1F2A37] px-6 py-3 text-sm font-semibold text-white"
          >
            Back to stays
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    {isMobileFlow ? (
    <>
    <div className="hv-ref-booking-screen hv-ref-guest-details-mobile">
      <main className="hv-ref-booking-main">
        <section className="hv-ref-booking-card">
          <div className="hv-ref-booking-row">
            <span>Secure premium checkout</span>
            <button type="button" onClick={handleBack}>‹ Back</button>
          </div>
          <h1>Guest information</h1>
          <p className="hv-ref-sub">Step 1 of 2 · Aligning details for {stayData?.title || stayData?.name || draft?.stayName || 'your stay'}.</p>
        </section>

        <section className="hv-ref-booking-card hv-ref-stay-mini">
          {stayData?.coverImage || stayData?.featured_image || stayData?.image_url ? (
            <img src={stayData.coverImage || stayData.featured_image || stayData.image_url} alt={stayData?.title || stayData?.name || 'Selected stay'} />
          ) : null}
          <div>
            <div className="hv-ref-eyebrow">Your stay</div>
            <h2>{stayData?.title || stayData?.name || draft?.stayName || 'Homevilas stay'}</h2>
            <p>{[stayData?.area, stayData?.city].filter(Boolean).join(', ') || draft?.location || stayData?.location || 'Location coming soon'}</p>
          </div>
        </section>

        {submitError ? <p className="hv-flow-error" role="alert">{submitError}</p> : null}

        <section className="hv-ref-booking-card">
          <div className="hv-ref-eyebrow">1 · Guest details & customizations</div>
          <h2>Primary traveller</h2>
          <div className="hv-ref-mobile-form">
            <label>Title<select {...register('title')}><option value="Mr">Mr</option><option value="Ms">Ms</option><option value="Mrs">Mrs</option><option value="Dr">Dr</option><option value="Mx">Mx</option></select></label>
            <label>First name<input {...register('firstName')} placeholder="First name" aria-invalid={!!formErrors.firstName} /></label>
            <label>Last name<input {...register('lastName')} placeholder="Last name" aria-invalid={!!formErrors.lastName} /></label>
            <label>Email<input type="email" {...register('email')} placeholder="Email ID" aria-invalid={!!formErrors.email} /></label>
            <label>Mobile number<div className="hv-ref-phone-field"><select {...register('countryCode')}><option value="+91">+91</option><option value="+1">+1</option><option value="+44">+44</option><option value="+61">+61</option><option value="+971">+971</option></select><input type="tel" inputMode="numeric" {...register('phoneNumber')} placeholder="Contact Number" aria-invalid={!!formErrors.phoneNumber} /></div></label>
            <label>Arrival window<input {...register('arrivalTime')} placeholder="2 PM - 4 PM" /></label>
            <label className="wide">Special requests<textarea {...register('specialRequests')} placeholder="Dietary preferences, celebration notes, or other requests" /></label>
          </div>
          <div className="hv-ref-field-errors">
            {formErrors.firstName ? <p>{formErrors.firstName.message}</p> : null}
            {formErrors.lastName ? <p>{formErrors.lastName.message}</p> : null}
            {formErrors.email ? <p>{formErrors.email.message}</p> : null}
            {formErrors.phoneNumber ? <p>{formErrors.phoneNumber.message}</p> : null}
            {formErrors.arrivalTime ? <p>{formErrors.arrivalTime.message}</p> : null}
          </div>
          <div className="hv-ref-guest-actions">
            <button type="button" onClick={handleAddGuest}>+ Add Guest</button>
            {showLoginPrompt ? <button type="button" onClick={() => setLoginOpen(true)}>Login with OTP</button> : null}
          </div>
        </section>

        {additionalGuests.length > 0 ? (
          <section className="hv-ref-booking-card">
            <div className="hv-ref-eyebrow">Additional guests</div>
            <div className="hv-ref-extra-list">
              {additionalGuests.map((guest, index) => (
                <div key={`mobile-guest-${index}`}>
                  <strong>Guest {index + 1}</strong>
                  <button type="button" onClick={() => handleRemoveGuest(index)}>Remove</button>
                  <input value={guest.firstName} onChange={(event) => handleGuestChange(index, 'firstName', event.target.value)} placeholder="First name" />
                  <input value={guest.lastName} onChange={(event) => handleGuestChange(index, 'lastName', event.target.value)} placeholder="Last name" />
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="hv-ref-booking-card hv-ref-price-card">
          {lineItems.map((item) => <div key={item.label}><span>{item.label}</span><b>{formatInr(item.value, pricingCurrency)}</b></div>)}
          <div className="total"><span>Final Payable Amount</span><b>{formatInr(totalPayable, pricingCurrency)}</b></div>
        </section>
      </main>
      <div className="hv-ref-bar">
        <button className="hv-ref-total" type="button"><span>Grand total</span><strong>{formatInr(totalPayable, pricingCurrency)}</strong></button>
        <button className="hv-ref-book" type="button" onClick={handleContinue} disabled={!canContinue}>{isSubmitting ? 'Processing…' : 'Review →'}</button>
      </div>
    </div>
    {loginOpen ? (
      <div className="hv-ref-login-modal" role="dialog" aria-modal="true" onClick={() => setLoginOpen(false)}>
        <div className="hv-ref-login-sheet" onClick={(event) => event.stopPropagation()}>
          <div className="hv-ref-booking-row">
            <span>Guest OTP</span>
            <button type="button" onClick={() => setLoginOpen(false)}>Close</button>
          </div>
          <h2>Login or signup with OTP</h2>
          <div className="hv-ref-login-tabs">
            <button type="button" className={loginIdentifier === 'phone' ? 'active' : ''} onClick={() => { setLoginIdentifier('phone'); setOtpSent(false); resetResendCooldown(); setLoginOtp(''); setLoginError(''); setLoginSuccess(''); }}>Mobile OTP</button>
            <button type="button" className={loginIdentifier === 'email' ? 'active' : ''} onClick={() => { setLoginIdentifier('email'); setOtpSent(false); resetResendCooldown(); setLoginOtp(''); setLoginError(''); setLoginSuccess(''); }}>Email OTP</button>
          </div>
          <div className="hv-ref-mobile-form">
            {loginRequiresSignup ? <label className="wide">Full name<input value={loginFullName} onChange={(event) => { setLoginFullName(event.target.value); setOtpSent(false); resetResendCooldown(); setLoginOtp(''); }} placeholder="Full name" /></label> : null}
            {(loginIdentifier === 'phone' || loginRequiresSignup) ? (
              <label className="wide">Mobile number<div className="hv-ref-phone-field"><select value={loginCountryCode} onChange={(event) => { setLoginCountryCode(event.target.value); setOtpSent(false); resetResendCooldown(); setLoginOtp(''); }}><option value="+91">+91</option><option value="+1">+1</option><option value="+44">+44</option><option value="+61">+61</option><option value="+971">+971</option></select><input type="tel" inputMode="numeric" value={loginMobile} onChange={(event) => { setLoginMobile(String(event.target.value || '').replace(/\D/g, '').slice(0, 10)); setOtpSent(false); resetResendCooldown(); setLoginOtp(''); if (loginError) setLoginError(''); }} placeholder="10-digit mobile number" /></div></label>
            ) : null}
            {(loginIdentifier === 'email' || loginRequiresSignup) ? <label className="wide">Email<input type="email" value={loginEmail} onChange={(event) => { setLoginEmail(event.target.value); setOtpSent(false); resetResendCooldown(); setLoginOtp(''); if (loginError) setLoginError(''); }} placeholder="name@email.com" /></label> : null}
            {otpSent ? <label className="wide">Enter OTP<input type="text" inputMode="numeric" value={loginOtp} onChange={(event) => { setLoginOtp(String(event.target.value || '').replace(/\D/g, '').slice(0, 8)); if (loginError) setLoginError(''); }} placeholder="6 digit OTP" /></label> : null}
          </div>
          {loginError ? <p className="hv-flow-error">{loginError}</p> : null}
          {loginSuccess ? <p className="hv-ref-login-success">{loginSuccess}</p> : null}
          <button type="button" className="hv-ref-pay-final" onClick={otpSent ? handleOtpVerify : handleOtpRequest} disabled={!loginReady || loginLoading}>{loginLoading ? 'Processing...' : otpSent ? 'Verify OTP' : 'Send OTP'}</button>
          {otpSent ? <button type="button" className="hv-ref-login-secondary" onClick={handleOtpResend} disabled={loginLoading || resendSeconds > 0}>{resendSeconds > 0 ? `Resend OTP in ${resendSeconds}s` : 'Resend OTP'}</button> : null}
        </div>
      </div>
    ) : null}
    </>
    ) : null}
    {!isMobileFlow ? (
    <div className="hv-desktop-flow relative min-h-screen overflow-x-clip bg-[#F6F2EC] pb-24">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 right-0 h-72 w-72 rounded-full bg-[#E8D9C7] opacity-70 blur-3xl" />
        <div className="absolute left-0 top-20 h-64 w-64 rounded-full bg-[#F3E6D8] opacity-60 blur-3xl" />
      </div>
      <div className="relative mx-auto flex w-full max-w-6xl flex-col gap-6 px-3 py-6 sm:px-4 sm:py-10">
        <div className="animate-fade-up">
          <CartHeader
            bookingId={effectiveBookingId}
            checkIn={draft?.checkIn || checkIn}
            checkOut={draft?.checkOut || checkOut}
            nights={draft?.nights || nights}
            guests={draft?.guests || guests}
            title="Guest details"
          />
        </div>
        {submitError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{submitError}</div>
        )}
        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-6 animate-fade-up-delay-1">
            <StayCard
              stay={stayData}
              checkIn={draft?.checkIn || checkIn}
              checkOut={draft?.checkOut || checkOut}
              nights={draft?.nights || nights}
              guests={draft?.guests || guests}
              roomType={draft?.roomType || null}
              roomCount={draft?.roomCount || 1}
              editLink={editLink}
            />
            <div className="rounded-2xl border border-[#E7DED4] bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.08)] sm:p-6">
              <h2 className="text-xl font-semibold text-slate-900">Guest details</h2>
              <p className="mt-1 text-sm text-slate-600">Share who is arriving so we can prepare the stay.</p>
              <div className="mt-5 grid gap-4 md:grid-cols-[120px_1fr_1fr]">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor="guestTitle">
                    Title
                  </label>
                  <select
                    id="guestTitle"
                    {...register('title')}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C78A58]/40"
                  >
                    <option value="Mr">Mr</option>
                    <option value="Ms">Ms</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Dr">Dr</option>
                    <option value="Mx">Mx</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor="guestFirstName">
                    Full name as per passport
                  </label>
                  <input
                    id="guestFirstName"
                    {...register('firstName')}
                    className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C78A58]/40"
                    placeholder="First name"
                    aria-invalid={!!formErrors.firstName}
                  />
                  {formErrors.firstName && <p className="mt-1 text-xs text-red-600">{formErrors.firstName.message}</p>}
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor="guestLastName">
                    &nbsp;
                  </label>
                  <input
                    id="guestLastName"
                    {...register('lastName')}
                    className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C78A58]/40"
                    placeholder="Last name"
                    aria-invalid={!!formErrors.lastName}
                  />
                  {formErrors.lastName && <p className="mt-1 text-xs text-red-600">{formErrors.lastName.message}</p>}
                </div>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor="guestEmail">
                    Email address
                    <span className="ml-1 normal-case tracking-normal text-slate-400">(Booking voucher will be sent to this email ID)</span>
                  </label>
                  <input
                    id="guestEmail"
                    type="email"
                    {...register('email')}
                    className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C78A58]/40"
                    placeholder="Email ID"
                    aria-invalid={!!formErrors.email}
                  />
                  {formErrors.email && <p className="mt-1 text-xs text-red-600">{formErrors.email.message}</p>}
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor="guestPhone">
                    Mobile number
                  </label>
                  <div className="mt-2 flex min-w-0 overflow-hidden rounded-xl border border-slate-200">
                    <select
                      id="guestCountryCode"
                      {...register('countryCode')}
                    className="w-20 shrink-0 border-r border-slate-200 bg-white px-2 py-2 text-sm focus-visible:outline-none"
                    >
                      <option value="+91">+91</option>
                      <option value="+1">+1</option>
                      <option value="+44">+44</option>
                      <option value="+61">+61</option>
                      <option value="+971">+971</option>
                    </select>
                    <input
                      id="guestPhone"
                      type="tel"
                      inputMode="numeric"
                      {...register('phoneNumber')}
                      className="min-w-0 flex-1 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C78A58]/40"
                      placeholder="Contact Number"
                      aria-invalid={!!formErrors.phoneNumber}
                    />
                  </div>
                  {formErrors.phoneNumber && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.phoneNumber.message}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor="guestArrival">
                    Arrival time (optional)
                  </label>
                  <input
                    id="guestArrival"
                    {...register('arrivalTime')}
                    className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C78A58]/40"
                    placeholder="Eg. 3:00 PM"
                  />
                  {formErrors.arrivalTime && <p className="mt-1 text-xs text-red-600">{formErrors.arrivalTime.message}</p>}
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                <button
                  type="button"
                  onClick={handleAddGuest}
                  className="font-semibold text-[#1F6FEB] hover:text-[#1557B0]"
                >
                  + Add Guest
                </button>
                <span className="text-xs text-slate-500">(Required for VISA purposes)</span>
              </div>
              {additionalGuests.length > 0 && (
                <div className="mt-4 space-y-4">
                  {additionalGuests.map((guest, index) => (
                    <div
                      key={`guest-${index}`}
                      className="rounded-xl border border-slate-200 bg-slate-50/60 p-4"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-700">Additional guest {index + 1}</p>
                        <button
                          type="button"
                          onClick={() => handleRemoveGuest(index)}
                          className="text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-700"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="mt-3 grid gap-4 md:grid-cols-[120px_1fr_1fr]">
                        <div>
                          <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor={`guest-${index}-title`}>
                            Title
                          </label>
                          <select
                            id={`guest-${index}-title`}
                            value={guest.title}
                            onChange={(event) => handleGuestChange(index, 'title', event.target.value)}
                            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C78A58]/40"
                          >
                            <option value="Mr">Mr</option>
                            <option value="Ms">Ms</option>
                            <option value="Mrs">Mrs</option>
                            <option value="Dr">Dr</option>
                            <option value="Mx">Mx</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor={`guest-${index}-firstName`}>
                            First name
                          </label>
                          <input
                            id={`guest-${index}-firstName`}
                            value={guest.firstName}
                            onChange={(event) => handleGuestChange(index, 'firstName', event.target.value)}
                            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C78A58]/40"
                            placeholder="First name"
                          />
                        </div>
                        <div>
                          <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor={`guest-${index}-lastName`}>
                            Last name
                          </label>
                          <input
                            id={`guest-${index}-lastName`}
                            value={guest.lastName}
                            onChange={(event) => handleGuestChange(index, 'lastName', event.target.value)}
                            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C78A58]/40"
                            placeholder="Last name"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4">
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor="guestRequests">
                  Special requests
                </label>
                <textarea
                  id="guestRequests"
                  {...register('specialRequests')}
                  className="mt-2 min-h-[120px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C78A58]/40"
                  placeholder="Dietary preferences, celebration notes, or other requests"
                />
                {formErrors.specialRequests && <p className="mt-1 text-xs text-red-600">{formErrors.specialRequests.message}</p>}
              </div>
              {showLoginPrompt && (
                <div className="mt-6 -mx-4 rounded-b-2xl border-t border-[#D7E6F6] bg-[#EDF5FF] px-4 py-4 sm:-mx-6 sm:px-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-700">
                    <span className="font-semibold text-slate-800">
                      Login to prefill traveller details and get access to secret deals.
                    </span>
                    <button
                      type="button"
                      onClick={() => setLoginOpen(true)}
                      className="rounded-full border border-[#1F2A37] px-5 py-2 text-xs font-semibold uppercase tracking-wide text-[#1F2A37] hover:bg-[#1F2A37] hover:text-white"
                    >
                      LOGIN
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="rounded-2xl border border-[#E7DED4] bg-white px-5 py-4 text-sm text-slate-600 shadow-[0_14px_34px_rgba(15,23,42,0.08)]">
              Need help? Call +918287703118 or email info@homevilas.com, meena@homevilas.com
            </div>
          </div>
          <div className="space-y-4 lg:sticky lg:top-24 lg:self-start animate-fade-up-delay-2">
            <SummaryCard
              stay={stayData}
              lineItems={lineItems}
              total={totalPayable}
              currency={pricingCurrency}
              footerNote="Exclusive of taxes. GST is shown separately."
            />
          </div>
        </div>
        <div className="hidden items-center justify-between gap-4 rounded-2xl border border-[#E7DED4] bg-white px-5 py-4 shadow-[0_14px_34px_rgba(15,23,42,0.08)] lg:flex">
          <button
            type="button"
            onClick={handleBack}
            className="rounded-full border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-700"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue}
            className="rounded-full bg-[#1F2A37] px-6 py-3 text-sm font-semibold text-white shadow-sm disabled:opacity-60"
          >
            Continue to review
          </button>
        </div>
      </div>
      {loginOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
          onClick={() => setLoginOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-white p-6 shadow-[0_30px_70px_rgba(15,23,42,0.35)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Guest OTP</p>
                <h3 className="text-lg font-semibold text-slate-900">Login or signup with OTP</h3>
              </div>
              <button
                type="button"
                onClick={() => setLoginOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600"
              >
                Close
              </button>
            </div>

            <div className="mt-5">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setLoginIdentifier('phone');
                    setOtpSent(false);
                    resetResendCooldown();
                    setLoginOtp('');
                    setLoginError('');
                    setLoginSuccess('');
                  }}
                  className={`rounded-full px-4 py-2 text-xs font-semibold ${
                    loginIdentifier === 'phone'
                      ? 'bg-[#1F2A37] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Mobile OTP
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setLoginIdentifier('email');
                    setOtpSent(false);
                    resetResendCooldown();
                    setLoginOtp('');
                    setLoginError('');
                    setLoginSuccess('');
                  }}
                  className={`rounded-full px-4 py-2 text-xs font-semibold ${
                    loginIdentifier === 'email'
                      ? 'bg-[#1F2A37] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Email OTP
                </button>
              </div>

              <div className="mt-4">
                <>
                  {loginRequiresSignup && (
                    <div className="mb-3">
                      <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor="loginFullName">
                        Full name
                      </label>
                      <input
                        id="loginFullName"
                        type="text"
                        value={loginFullName}
                        onChange={(event) => {
                          setLoginFullName(event.target.value);
                          setOtpSent(false);
                          resetResendCooldown();
                          setLoginOtp('');
                          setLoginSuccess('');
                          if (loginError) setLoginError('');
                        }}
                        className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C78A58]/40"
                        placeholder="Full name"
                      />
                    </div>
                  )}
                  {(loginIdentifier === 'phone' || loginRequiresSignup) && (
                    <div className="mb-3">
                    <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor="loginMobile">
                      Mobile number
                    </label>
                    <div className="mt-2 flex items-center overflow-hidden rounded-xl border border-slate-200">
                      <div className="flex items-center gap-2 border-r border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500">
                        <span>IN</span>
                        <select
                          value={loginCountryCode}
                          onChange={(event) => {
                            setLoginCountryCode(event.target.value);
                            setOtpSent(false);
                            resetResendCooldown();
                            setLoginOtp('');
                            setLoginSuccess('');
                          }}
                          className="bg-transparent text-xs font-semibold text-slate-700 focus-visible:outline-none"
                        >
                          <option value="+91">+91</option>
                          <option value="+1">+1</option>
                          <option value="+44">+44</option>
                          <option value="+61">+61</option>
                          <option value="+971">+971</option>
                        </select>
                      </div>
                      <input
                        id="loginMobile"
                        type="tel"
                        inputMode="numeric"
                        value={loginMobile}
                        onChange={(event) => {
                          setLoginMobile(String(event.target.value || '').replace(/\D/g, '').slice(0, 10));
                          setOtpSent(false);
                          resetResendCooldown();
                          setLoginOtp('');
                          setLoginSuccess('');
                          if (loginError) setLoginError('');
                        }}
                        className="w-full px-3 py-2 text-sm focus-visible:outline-none"
                        placeholder="10-digit mobile number"
                        maxLength={10}
                        pattern="[0-9]{10}"
                      />
                    </div>
                    </div>
                  )}
                  {(loginIdentifier === 'email' || loginRequiresSignup) && (
                    <div className="mb-3">
                      <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor="loginEmail">
                        Email address
                      </label>
                      <input
                        id="loginEmail"
                        type="email"
                        value={loginEmail}
                        onChange={(event) => {
                          setLoginEmail(event.target.value);
                          setOtpSent(false);
                          resetResendCooldown();
                          setLoginOtp('');
                          setLoginSuccess('');
                          if (loginError) setLoginError('');
                        }}
                        className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C78A58]/40"
                        placeholder="name@email.com"
                      />
                    </div>
                  )}
                    {otpSent && (
                      <div className="mt-3">
                        <label className="text-xs uppercase tracking-[0.2em] text-slate-500" htmlFor="loginOtp">
                          Enter OTP
                        </label>
                        <input
                          id="loginOtp"
                          type="text"
                          inputMode="numeric"
                          value={loginOtp}
                          onChange={(event) => {
                            setLoginOtp(String(event.target.value || '').replace(/\D/g, '').slice(0, 8));
                            if (loginError) setLoginError('');
                          }}
                          className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C78A58]/40"
                          placeholder="6 digit OTP"
                          maxLength={8}
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={otpSent ? handleOtpVerify : handleOtpRequest}
                      disabled={!loginReady || loginLoading}
                      className="mt-4 w-full rounded-xl bg-slate-200 px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
                      style={{ backgroundColor: loginReady ? '#1F2A37' : '#D1D5DB' }}
                    >
                      {loginLoading ? 'Processing...' : 'Continue'}
                    </button>
                    {otpSent && (
                      <button
                        type="button"
                        onClick={handleOtpResend}
                        disabled={loginLoading || resendSeconds > 0}
                        className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {resendSeconds > 0 ? `Resend OTP in ${resendSeconds}s` : 'Resend OTP'}
                      </button>
                    )}
                    {loginSuccess && <p className="mt-2 text-xs text-emerald-600">{loginSuccess}</p>}
                </>
                {loginError && <p className="mt-2 text-xs text-red-600">{loginError}</p>}
              </div>
            </div>

            <p className="mt-5 text-[11px] text-slate-500">
              By proceeding, you agree to Homevilas&apos; Privacy Policy, User Agreement and T&Cs.
            </p>
            <div className="mt-2 text-right">
              <a href={loginHref} className="text-xs font-semibold text-slate-500 underline">
                Open full login page
              </a>
            </div>
          </div>
        </div>
      )}
      <MobileStickyBar
        total={totalPayable}
        lineItems={lineItems}
        currency={pricingCurrency}
        onContinue={handleContinue}
        disabled={!canContinue}
        label={isSubmitting ? 'Processing' : 'Continue'}
        onBack={handleBack}
        backLabel="Back"
      />
    </div>
    ) : null}
    </>
  );
};

export default GuestDetailsPage;
